'use memo';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import { DataTable } from '@/components/DataTable';
import { SimpleDatePicker } from '@/components/DatePicker/SimpleDatePicker';
import { ConfirmActionDialog } from '@/components/Dialog/ConfirmActionDialog';
import { FormItem } from '@/components/FormItem';
import { FillScreen, HStack, VStack } from '@/components/Layout';
import { TooltipWrapper } from '@/components/Tooltip/TooltipWrapper';
import { Text } from '@/components/Typography/Text';
import { useToast } from '@/components/ui/use-toast';
import { DATA_CONNECTOR_FEATURE_FLAGS } from '@/features/data-connector/constants/feature-flags';
import { handleApiError } from '@/lib/api/error';
import { orpc } from '@/lib/api/orpc';
import { featureFlags } from '@/lib/env';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { QUERY_PRESETS } from '@/lib/query';
import { useOutboundTaskTable } from './-hooks/useTable';
export const Route = createFileRoute('/_staff/settings/data-connector/outbound/data-pipeline/$id/_pipeline-detail/execution-results/')({
  component: OutboundExecutionResultsPage
});
function OutboundExecutionResultsPage() {
  const {
    id: pipelineId
  } = Route.useParams();
  const {
    t,
    i18n
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.outboundDataPipeline.detail.executionResults'
  });
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const {
    table,
    displayState
  } = useOutboundTaskTable(pipelineId);

  // 商談デモ向けの FeatureFlag。ON のテナントでのみ、実行時に「特定条件で絞り込んで
  // 出力」するための基準日をモーダルで選ばせる。OFF（既定）では従来どおり即時実行する。
  const isOutputFilterEnabled = featureFlags.check(DATA_CONNECTOR_FEATURE_FLAGS.OUTBOUND_OUTPUT_FILTER);
  const [isExecuteDialogOpen, setIsExecuteDialogOpen] = useState(false);
  const [baseDate, setBaseDate] = useState<Date | undefined>(undefined);

  // 実行計画（運用モード / トリガーモード）で手動実行の可否を判定する（IN と同じ挙動）。
  const detailQuery = useQuery({
    ...orpc.outboundDataPipeline.get.queryOptions({
      input: {
        id: pipelineId
      }
    }),
    ...QUERY_PRESETS.detail()
  });
  const isPaused = detailQuery.data?.executionPlan?.operationMode === 'PAUSED';
  const isScheduled = detailQuery.data?.executionPlan?.triggerMode === 'SCHEDULE';
  const isExecuteDisabled = isPaused || isScheduled;
  const executeDisabledReason = isPaused ? t('executeDisabledPaused') : isScheduled ? t('executeDisabledScheduled') : undefined;
  const executeMutation = useMutation(orpc.outboundDataPipelineTask.executeManual.mutationOptions({
    onSuccess: () => {
      // バッチ起動〜Task 作成までラグがあり即時には一覧へ反映されないため、
      // 「実行を開始した・時間がかかる・画面は自動更新されない」ことをトーストで明示する。
      setIsExecuteDialogOpen(false);
      toast({
        title: t('executeStartedTitle'),
        description: t('executeStartedDescription')
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.outboundDataPipelineTask.listTasks.key({
          input: {
            pipelineId
          }
        })
      });
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        description: message,
        variant: 'destructive'
      });
    }
  }));
  const handleExecuteClick = () => {
    if (isOutputFilterEnabled) {
      setBaseDate(undefined);
      setIsExecuteDialogOpen(true);
      return;
    }
    executeMutation.mutate({
      pipelineId
    });
  };
  const handleConfirmExecute = () => {
    if (executeMutation.isPending) return;
    // TODO(DC-8348): baseDate をバックエンドの出力フィルタ条件として送信する。
    // 現状は BE が基準日での絞り込みに未対応のため、フロントのみのプレビュー
    // （商談デモ用）で baseDate は実行リクエストに含めていない。BE 対応後に付与する。
    executeMutation.mutate({
      pipelineId
    });
  };
  return <FillScreen className="flex flex-col gap-4 px-12 py-5">
      <HStack className="justify-end gap-2">
        <TooltipWrapper content={executeDisabledReason}>
          <ActionButton variant="secondary" icon="RocketIcon" onClick={handleExecuteClick} isLoading={executeMutation.isPending} disabled={isExecuteDisabled}>
            {t('execute')}
          </ActionButton>
        </TooltipWrapper>
      </HStack>
      <div className="min-h-0 flex-1 overflow-auto">
        <DataTable table={table} displayState={displayState} onRowClick={null} />
      </div>

      {isOutputFilterEnabled && <ConfirmActionDialog isOpen={isExecuteDialogOpen} onClose={() => setIsExecuteDialogOpen(false)} onConfirm={handleConfirmExecute} isLoading={executeMutation.isPending} title={t('executeDialog.title')} okLabel={t('executeDialog.confirm')} description={<VStack className="gap-3 py-2">
              <Text variant="p">{t('executeDialog.description')}</Text>
              <FormItem fieldTitle={t('executeDialog.baseDate')} fieldInput={<SimpleDatePicker value={baseDate} onChange={setBaseDate} placeholder={t('executeDialog.baseDatePlaceholder')} allowEmpty className="w-full" />} />
            </VStack>} />}
    </FillScreen>;
}